import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAuth, setAuth, clearAuth, getRefreshToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const axiosInstance = axios.create({
  baseURL: API_URL,
});

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const onRefreshFailed = () => {
  refreshSubscribers = [];
};

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const auth = getAuth();
    if (auth?.access_token && config.headers) {
      config.headers.Authorization = `Bearer ${auth.access_token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle 401 with automatic token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // If not 401 or already retried, reject immediately
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // If already refreshing, queue this request to retry after refresh completes
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((token: string) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          resolve(axiosInstance(originalRequest));
        });
      });
    }

    // Mark as retrying and start the refresh process
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // Call refresh endpoint using a fresh axios instance (not the intercepted one)
      // Note: refresh endpoint returns { access_token, refresh_token, expires_in } - no user object
      const response = await axios.post<{ access_token: string; refresh_token: string; expires_in: string }>(
        `${API_URL}/auth/refresh`,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const { access_token, refresh_token } = response.data;

      // Update stored auth with new tokens (keep existing user data)
      const currentAuth = getAuth();
      if (currentAuth) {
        setAuth({
          ...currentAuth,
          access_token,
          refresh_token,
        });
      }

      // Notify all queued requests that refresh is complete
      onRefreshed(access_token);
      isRefreshing = false;

      // Retry the original request with the new token
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
      }
      return axiosInstance(originalRequest);

    } catch (refreshError) {
      // Refresh failed - clear auth and redirect to login
      isRefreshing = false;
      onRefreshFailed();
      clearAuth();

      // Use replace to prevent back-button issues
      window.location.replace('/login');

      return Promise.reject(refreshError);
    }
  }
);

export default axiosInstance;
